import { RebateTier } from '@general/js/enums';
import { OperationType, VeteranCardTypeOptions, EligibilityTypes } from '@/enums';
import { formatFormDate, removeWhitespaceFromString, isEmptyString } from '@general/js/utils';
import { mapMedicareCardCode } from '@/utils/CRMUtils';

export const mapVeteranCardConditions = (veteranCardConditions) => {
  return veteranCardConditions.map((condition) => {
    const mappedCondition = {
      op: condition.op,
      id: condition.id,
      code: condition.returnValue,
      startDate: condition.startDate,
    };

    if (condition.endDate) {
      mappedCondition.endDate = condition.endDate;
    }
    return mappedCondition;
  });
};

export const convertVeteranCardTypeToCode = (veteranCardType) => {
  if (veteranCardType) {
    if (veteranCardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_GOLD_CARD) {
      return VeteranCardTypeOptions.VETERAN_GOLD_CODE;
    }
    if (veteranCardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD) {
      return VeteranCardTypeOptions.VETERAN_WHITE_CODE;
    }
  }

  return null;
};

export const mapVeteranCardPayload = (initialVeteranCard, newVeteranCard, noLongerEligibleForVeteran = false) => {
  const initialCardObj = {
    startDate: initialVeteranCard.startDate,
    cardType: convertVeteranCardTypeToCode(initialVeteranCard.veteranCardType),
    acceptedConditions: mapVeteranCardConditions(initialVeteranCard.veteranCardConditionOptions),
    op: initialVeteranCard.op,
    id: initialVeteranCard.id,
  };

  const newCardObj = {
    startDate: newVeteranCard.startDate,
    cardType: convertVeteranCardTypeToCode(newVeteranCard.veteranCardType),
    acceptedConditions:
      newVeteranCard.veteranCardType?.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD ? mapVeteranCardConditions(newVeteranCard.veteranCardConditionOptions) : [],
    op: newVeteranCard.op,
  };

  // if no longer eligibility for veteran card, then remove existing ones
  if (initialVeteranCard.haveVeteranCard && noLongerEligibleForVeteran) {
    if (initialVeteranCard.veteranCardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD) {
      // set all conditions to remove if they changed to no longer having veteran card
      initialCardObj.acceptedConditions = initialCardObj.acceptedConditions.map((condition) => {
        return {
          ...condition,
          op: OperationType.REMOVE, // EAI will do the end dating for removed cards
        };
      });
    }
    initialCardObj.op = OperationType.REMOVE; // EAI will do the end dating for removed cards
    return [initialCardObj];
  }

  // if initially had veteran card and still has veteran card
  if (initialVeteranCard.haveVeteranCard && newVeteranCard.haveVeteranCard) {
    if (initialVeteranCard.op === OperationType.REMOVE && newVeteranCard.op === OperationType.ADD) {
      if (initialVeteranCard.veteranCardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD) {
        initialCardObj.acceptedConditions = initialCardObj.acceptedConditions.map((condition) => {
          return {
            ...condition,
            op: OperationType.REMOVE, // EAI will do the end dating for removed cards
          };
        });
      }
      if (initialCardObj.endDate) {
        initialCardObj.endDate = initialVeteranCard.endDate;
      }
      return [initialCardObj, newCardObj];
    }

    // if both cards are veteran white cards, check for changed accepted conditions
    if (
      initialVeteranCard.veteranCardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD &&
      newVeteranCard.veteranCardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD
    ) {
      // take all conditions from new object and 'replaced' conditions (removed conditions) from initial veteran card
      const replacedConditions = initialCardObj.acceptedConditions.filter((condition) => {
        return condition.op === OperationType.REMOVE;
      });
      newCardObj.acceptedConditions = newCardObj.acceptedConditions.concat(replacedConditions);

      // return new card object with concatenated replaced conditions
      if (
        newCardObj.acceptedConditions.some((condition) => {
          return condition.op !== OperationType.NONE;
        })
      ) {
        newCardObj.op = OperationType.REPLACE; // if any accepted condition has changed, card is now replaced
        newCardObj.id = initialCardObj.id; // send initial card id for 'replace' card
      }
      return [newCardObj]; // otherwise, card is op: 'none'
    }

    if (newVeteranCard.op === OperationType.NONE) {
      return [initialCardObj];
    }
  } else if (initialVeteranCard.haveVeteranCard) {
    if (initialVeteranCard.veteranCardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD) {
      // set all conditions to remove if they changed to no longer having veteran card
      initialCardObj.acceptedConditions = initialCardObj.acceptedConditions.map((condition) => {
        return {
          ...condition,
          op: OperationType.REMOVE, // EAI will do the end dating for removed cards
        };
      });
    }
    initialCardObj.op = OperationType.REMOVE; // EAI will do the end dating for removed cards
    return [initialCardObj];
  } else if (newVeteranCard.haveVeteranCard) {
    return [newCardObj]; // return new veteran card when there was no veteran card initially
  }
  return [];
};

/**
 * This function is used to craft the Concession Cards `cardList` payload used for Change Cover submission.
 * @param {boolean} initialHaveConcessionCard Did the initial partner/current partner initially have a concession card?
 * @param {Array} initialConcessionCardList If yes, the initial concession card list
 * @param {boolean} newHaveConcessionCard Does the current partner being added have a concession card?
 * @param {Array} newConcessionCardList If yes, current concession card list
 * @param {boolean} noLongerEligibleForConcession true if the member is no longer eligible for concession cards. false by default.
 * @param {boolean} showConcessionCardDetailsForm boolean to determine if concession card details form(card no, type, expiry, etc) is displayed(based on concession card business rules)
 * @returns Mapped array for concession cards with appropriate operations as required
 */
export const mapConcessionCardPayload = (
  initialHaveConcessionCard,
  initialConcessionCardList,
  newHaveConcessionCard,
  newConcessionCardList,
  noLongerEligibleForConcession = false,
  showConcessionCardDetailsForm,
) => {
  const initialCardList = initialConcessionCardList.map((card) => {
    return {
      op: card.op,
      id: card.id,
      cardType: card.name,
      cardNumber: card.number,
      expiryDate: formatFormDate(card.expire), // optional for some cards
      startDate: formatFormDate(card.startDate), // YYYY-MM-DD
    };
  });

  const newCardList = newConcessionCardList.map((card) => {
    return {
      id: card.id,
      op: card.op,
      cardType: card.name,
      cardNumber: card.number,
      expiryDate: formatFormDate(card.expire), // optional for some cards
      startDate: formatFormDate(card.startDate), // YYYY-MM-DD
    };
  });

  // if had initial concession cards, and no longer eligible for concession, remove all cards in list
  if (noLongerEligibleForConcession) {
    return initialCardList.map((card) => {
      return {
        ...card,
        op: OperationType.REMOVE,
      };
    });
  }

  // if previously had concession card(CC) and currently have it too, but new state doesn't require card details(showConcessionCardDetailsForm=false), only Yes/No, then remove all old cards
  if (initialHaveConcessionCard && newHaveConcessionCard && !showConcessionCardDetailsForm) {
    return initialCardList.map((card) => {
      return {
        ...card,
        op: OperationType.REMOVE,
      };
    });
  }

  // if have a new and old concession card list, combine them and add all 'remove' with cards in new list
  if (initialHaveConcessionCard && newHaveConcessionCard) {
    const removedConcessionCards = initialCardList.filter((card) => {
      return card.op === OperationType.REMOVE;
    });
    return newCardList.concat(removedConcessionCards);
  }

  // if initially had concession card but now does not, end date all existing cards
  if (initialHaveConcessionCard) {
    return initialCardList.map((card) => {
      return {
        ...card,
        op: OperationType.REMOVE,
      };
    });
  }

  // return the new card list if there was no initial card list (all operation should be ADD)
  if (newHaveConcessionCard) {
    return newCardList;
  }
  return [];
};

/**
 * Format medicare card details
 *
 * @param {object} card, medicare card details in rebate step
 * @param {boolean} isShortCardType, if true, will only return the first letter of card type (e.g. "Y"), otherwise return full description
 * @returns formatted medicare card details
 */
export const formatMedicareCard = (card, isShortCardType = true) => {
  if (card != null && Object.keys(card).length) {
    const tempCard = {
      cardType: mapMedicareCardCode(card.cardType, isShortCardType),
      cardNumber: removeWhitespaceFromString(card.cardNumber),
      firstName: card.firstName,
      lastName: card.lastName,
    };
    // Only existing medicare card has card id
    if (!isEmptyString(card.id)) {
      tempCard.id = card.id;
    }
    if (card.expiryDate) {
      tempCard.expiryDate = formatFormDate(card.expiryDate, true);
    }
    return tempCard;
  }

  return null;
};

export const mapRebatePayload = (rebateTier) => {
  // "none" is not acceptable value for CRM
  if (rebateTier && rebateTier.toLowerCase() !== RebateTier.NONE) {
    return {
      rebateTier,
      declarationAcceptance: true,
      declarationAcknowledgement: true,
    };
  }
  return null;
};

/**
 * Check if the fund has automatic transfer on or off
 * TODO: May be good to refactor change product/add remove if any chance in the future to use this instead of the it in the mixin
 * since it's being used in one of the util functions (Partner.js)
 * @param {string} code fund code
 * @param {Array.<Object>} formatted `fundNames` array of available funds fetched from OHI. Each `object` should have a `value` key to be compared with the `code` parameter.
 * @returns True if isFundAutomaticTransfer is on for the fund, otherwise False
 */
export const isFundAutomaticTransfer = (code, fundNames) => {
  const fund = fundNames.find((f) => {
    return f.value === code;
  });
  if (fund && fund.isAutomaticTransfer) {
    return fund.isAutomaticTransfer;
  }
  return false;
};

/**
 * Convert eligibility name from API response to eligibility type name
 * @param {string} eligibilityName String value of eligibility returned in response
 * @returns {string} Eligibility type name for the given name in the EligibilityType category. Eg, ADF, Ex-Serving, etc or '' if not found
 */
export const mapEligibilityNames = (eligibilityName) => {
  let currentEligibility = '';
  if (EligibilityTypes.SERVING.includes(eligibilityName)) {
    currentEligibility = EligibilityTypes.SERVING[0];
  } else if (EligibilityTypes.EX_SERVING.includes(eligibilityName)) {
    currentEligibility = EligibilityTypes.EX_SERVING[0];
  } else if (EligibilityTypes.WORK_CONNECTION.includes(eligibilityName)) {
    currentEligibility = EligibilityTypes.WORK_CONNECTION[0];
  } else if (EligibilityTypes.RELATED.includes(eligibilityName)) {
    currentEligibility = EligibilityTypes.RELATED[0];
  }
  return currentEligibility;
};

/**
 * Convert pregnancy flag string to boolean
 * @param {string|boolean} pregnancyFlag String value of excludePregnancy returned in response
 * @returns {boolean} true if "true", false if "false", otherwise null 
 */
export const convertPregnancyFlagStringToBool = (pregnancyFlag) => {
  if (pregnancyFlag === 'true' || pregnancyFlag === true) {
    return true;
  } 
  if (pregnancyFlag === 'false' || pregnancyFlag === false) {
    return false;
  }
  return null;
};

